import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, LogFilters } from '../../lib/api';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function LogsPage() {
  const [filters, setFilters] = useState<LogFilters>({
    page: 1,
    limit: 50,
  });

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters({ ...filters, limit: newLimit, page: 1 });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin-logs', filters],
    queryFn: () => api.admin.logs.list(filters),
  });

  return (
    <div className="flex flex-col h-[calc(100vh-48px)] overflow-hidden">
      <div className="page-header">
        <h1 className="page-title">Application Logs</h1>
      </div>

      <div className="card mb-4">
        <div className="flex items-center gap-4">
          <select
            className="form-input !w-auto"
            value={filters.level || ''}
            onChange={(e) => setFilters({ ...filters, level: e.target.value || undefined, page: 1 })}
          >
            <option value="">All Levels</option>
            <option value="debug">Debug</option>
            <option value="info">Info</option>
            <option value="warn">Warn</option>
            <option value="error">Error</option>
          </select>

          <input
            type="text"
            className="form-input !w-[250px]"
            placeholder="Search messages..."
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined, page: 1 })}
          />

          <input
            type="text"
            className="form-input !w-[150px]"
            placeholder="Context (e.g., auth)"
            value={filters.context || ''}
            onChange={(e) => setFilters({ ...filters, context: e.target.value || undefined, page: 1 })}
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted">Loading...</p>
      ) : (
        <>
          <div className="card flex-1 flex flex-col overflow-hidden min-h-0 !mb-0">
            <div className="flex-1 overflow-y-auto overflow-x-auto">
              <table className="table m-0">
                <thead className="sticky top-0 bg-table-header z-10">
                  <tr>
                    <th className="w-[160px] border-t-0">Timestamp</th>
                    <th className="w-[80px] border-t-0">Level</th>
                    <th className="w-[100px] border-t-0">Context</th>
                    <th className="border-t-0">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((log) => (
                    <tr key={log.id}>
                      <td className="text-muted text-xs">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td>
                        <span className={`badge badge-${log.level}`}>{capitalize(log.level)}</span>
                      </td>
                      <td className="text-muted">{log.context || '-'}</td>
                      <td className="break-words">{log.message}</td>
                    </tr>
                  ))}
                  {data?.data.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-muted">No logs found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {data && (
            <div className="flex flex-col gap-3 mt-4 px-4 py-3 bg-surface border border-border rounded flex-shrink-0">
              <div className="flex justify-between items-center text-[13px] text-foreground-muted">
                <span className="font-medium">
                  {data.meta.total} total logs
                </span>
                <span>
                  Showing {((filters.page || 1) - 1) * (filters.limit || 50) + 1}
                  –{Math.min((filters.page || 1) * (filters.limit || 50), data.meta.total)} of {data.meta.total}
                </span>
              </div>

              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-2 text-[13px] text-foreground-muted">
                  <label>Per page:</label>
                  <select
                    className="form-input !w-auto px-2 py-1 text-[13px]"
                    value={filters.limit || 50}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                {data.meta.totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      className="btn btn-secondary px-2.5 py-1 text-xs"
                      disabled={filters.page === 1}
                      onClick={() => handlePageChange(1)}
                      title="First page"
                    >
                      ««
                    </button>
                    <button
                      className="btn btn-secondary px-2.5 py-1 text-xs"
                      disabled={filters.page === 1}
                      onClick={() => handlePageChange((filters.page || 1) - 1)}
                      title="Previous page"
                    >
                      ‹ Prev
                    </button>
                    <span className="text-[13px] text-foreground px-2 min-w-[120px] text-center">
                      Page {filters.page} of {data.meta.totalPages}
                    </span>
                    <button
                      className="btn btn-secondary px-2.5 py-1 text-xs"
                      disabled={filters.page === data.meta.totalPages}
                      onClick={() => handlePageChange((filters.page || 1) + 1)}
                      title="Next page"
                    >
                      Next ›
                    </button>
                    <button
                      className="btn btn-secondary px-2.5 py-1 text-xs"
                      disabled={filters.page === data.meta.totalPages}
                      onClick={() => handlePageChange(data.meta.totalPages)}
                      title="Last page"
                    >
                      »»
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
